import { Panel, PanelType, PrimaryButton, Spinner, Stack } from "@fluentui/react";
import { FluentProvider, webLightTheme } from "@fluentui/react-components";
import { useBoolean } from "@fluentui/react-hooks";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { CSSProperties, Fragment, FunctionComponent, ReactNode, useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { useNavigate } from "react-router-dom";
import {
  useLazyGetAIInsightDataQuery,
  useLazyGetMissingProductsInsightDataQuery,
  AIInsightData
} from "../slices/ExploreCustomers.slice";
import { logEvent, logException } from "./AppInsightsHelper";
import DisplayAPIsException from "../BasicComponents/DisplayAPIsExceptionAlert";

interface InsightsComponentProps {
  metricId?: string;
  panelTitle: ReactNode;
  tenantId?: string;
  tenantName?: string;
  buttonStyle?: CSSProperties; // Optional style prop
}

const AIInsightsButtonWithPanel: FunctionComponent<InsightsComponentProps> = ({
  metricId,
  panelTitle,
  tenantId,
  tenantName,
  buttonStyle
}) => {
  // Properties
  const navigate = useNavigate();
  const [isOpen, { setTrue: openPanel, setFalse: dismissPanel }] = useBoolean(false);
  const [triggerAIInsightDataQuery, { isLoading: isAIInsightLoading, data, error: aiInsightError }] = useLazyGetAIInsightDataQuery();
  const [triggerInsightDataQuery, { isLoading: isMissingProductsLoading, data: missingProductsData, error: missingProductsError }] =
    useLazyGetMissingProductsInsightDataQuery();

  // Cache key for sessionStorage
  const CACHE_KEY = "aiInsightsCache";

  // Helper functions for cache management
  const getCache = (): { [tenantId: string]: { [metricId: string]: AIInsightData[] } } => {
    try {
      const cached = sessionStorage.getItem(CACHE_KEY);
      return cached ? JSON.parse(cached) : {};
    } catch (error) {
      return {};
    }
  };

  const setCache = (cache: { [tenantId: string]: { [metricId: string]: AIInsightData[] } }) => {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify(cache));
    } catch (error) {
      logException("AIInsightsButtonWithPanel", "AIInsightsButtonWithPanel", "setCache", error, {
        tenantId,
        metricId,
        cacheSize: Object.keys(cache).length
      });
    }
  }; // Current data to display (either from cache or from API)
  const [currentAIInsightData, setCurrentAIInsightData] = useState<AIInsightData[] | undefined>(undefined);

  // Effect to cache AI insights data when it arrives from API
  useEffect(() => {
    if (data && Array.isArray(data) && tenantId) {
      // Group insights by MetricId
      const insightsByMetricId: { [metricId: string]: AIInsightData[] } = {};

      data.forEach((insight: AIInsightData) => {
        if (insight.metricId) {
          if (!insightsByMetricId[insight.metricId]) {
            insightsByMetricId[insight.metricId] = [];
          }
          insightsByMetricId[insight.metricId].push(insight);
        }
      });

      // Get current cache and update it
      const currentCache = getCache();
      const updatedCache = {
        ...currentCache,
        [tenantId]: {
          ...currentCache[tenantId],
          ...insightsByMetricId
        }
      };
      setCache(updatedCache);

      // Set current data to display for the requested metric ID
      if (metricId && insightsByMetricId[metricId]) {
        setCurrentAIInsightData(insightsByMetricId[metricId]);
      }
    }
  }, [data, metricId, tenantId]);

  // Effect to update current data when metricId changes and data is already cached
  useEffect(() => {
    if (tenantId && metricId) {
      const currentCache = getCache();
      const cachedData = currentCache[tenantId]?.[metricId];
      if (cachedData) {
        setCurrentAIInsightData(cachedData);
      }
    }
  }, [metricId, tenantId]);

  const columns: GridColDef[] = [
    {
      headerName: "Product Family",
      field: "productFamily",
      width: 280,
      sortable: false,
      filterable: false,
      disableColumnMenu: true
    },
    {
      headerName: "Product Name",
      field: "productName",
      width: 250,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      flex: 1,
      renderCell: ({ row: { productName } }: any) => (
        <a
          style={{
            color: "rgb(0, 120, 212)",
            textDecoration: "none",
            fontSize: 12,
            overflow: "hidden",
            textOverflow: "ellipsis"
          }}
          href="#"
          onClick={() => {
            navigate("/productview?productname=" + productName);
          }}
          title={productName}
        >
          {productName}
        </a>
      )
    }
  ];

  const customProps = {
    tenantId: tenantId,
    tenantName: tenantName
  };

  const componentTitle: string = typeof panelTitle === "string" ? panelTitle : "Summary Insights";

  // Events
  const handleAIInsightsButtonClick = () => {
    openPanel();

    // Check if AI insights data is already cached for this tenant ID and metric ID
    const currentCache = getCache();
    const cachedAIInsight = tenantId && metricId ? currentCache[tenantId]?.[metricId] : undefined;

    if (cachedAIInsight) {
      // Use cached data immediately
      setCurrentAIInsightData(cachedAIInsight);
    } else if (tenantId && metricId) {
      // Only make API call if data is not cached and we have required params
      triggerAIInsightDataQuery({ metricId, tenantId }, true);
    }

    // Always fetch missing products data (no caching for now)
    if (tenantId) {
      triggerInsightDataQuery({ tenantId }, true);
    }

    logEvent("AIInsights Button Clicked", "Customer Page", componentTitle, customProps);
  };

  const renderSpinner = () =>
    (isAIInsightLoading || isMissingProductsLoading) && (
      <Stack styles={{ root: { alignItems: "center", justifyContent: "center", height: "100%" } }}>
        <Spinner label="Loading" />
      </Stack>
    );

  const renderInsightsContent = () => {
    const hasAIInsights = currentAIInsightData && currentAIInsightData.length > 0;
    const aiInsightToDisplay = hasAIInsights ? currentAIInsightData[0] : null;

    // Parse missing products data if it's a string
    let parsedMissingProducts: any[] = [];
    if (missingProductsData) {
      try {
        parsedMissingProducts =
          typeof missingProductsData === "string" ? JSON.parse(missingProductsData) : missingProductsData;
      } catch (error) {
        logException("AIInsightsButtonWithPanel", "AIInsightsButtonWithPanel", "parseMissingProductsData", error, {
          tenantId,
          metricId,
          dataType: typeof missingProductsData
        });
        parsedMissingProducts = [];
      }
    }

    if (aiInsightError && missingProductsError) {
      return <DisplayAPIsException error={aiInsightError || missingProductsError} height={250} />;
    }

    if(!aiInsightError && !missingProductsError && !hasAIInsights && parsedMissingProducts.length === 0) {
      return <div>No insights available.</div>;
    }

    return (
      !isAIInsightLoading &&
      !isMissingProductsLoading &&
      hasAIInsights && (
        <FluentProvider theme={webLightTheme}>
          <div>
            {aiInsightToDisplay?.paragraph && (
              <div style={{ textAlign: "justify" }}>
                <ReactMarkdown>{aiInsightToDisplay.paragraph}</ReactMarkdown>
              </div>
            )}

            {/* Render highlights */}
            {[1, 2, 3, 4, 5].map((num) => {
              const titleKey = `highlight${num}Title` as keyof AIInsightData;
              const descKey = `highlight${num}Description` as keyof AIInsightData;
              const title = aiInsightToDisplay?.[titleKey];
              const description = aiInsightToDisplay?.[descKey];

              // Only render if both title and description are available (not null/undefined/empty)
              if (title && description) {
                return (
                  <div key={num} style={{ marginBottom: "16px" }}>
                    <h4 style={{ fontSize: 16, fontWeight: 700, marginBottom: "8px" }}>{title}</h4>
                    <div style={{ textAlign: "justify" }}>
                      <ReactMarkdown>{description}</ReactMarkdown>
                    </div>
                  </div>
                );
              }
              return null;
            })}
          </div>

          {/* Render NHS Products section only when data exists */}
          {parsedMissingProducts.length > 0 && (
            <>
              <h3 style={{ fontSize: 16, fontWeight: 700 }}>{`List of Products not used by ${tenantName}`}</h3>
              <DataGrid
                rows={parsedMissingProducts}
                columns={columns}
                getRowId={(row) => row.productId}
                showCellVerticalBorder
                hideFooter
              />
            </>
          )}
        </FluentProvider>
      )
    );
  };

  const renderAIInsightsButton = () => (
    <PrimaryButton
      onClick={handleAIInsightsButtonClick}
      style={buttonStyle ?? { position: "relative", left: "92%" }} // Use prop or default
      text="AI Insights"
    />
  );

  const renderPanelHeader = () => <div style={{ fontSize: 26, fontWeight: 700, paddingLeft: 32 }}>{panelTitle}</div>;

  const renderPanel = () => (
    <Panel
      onRenderHeader={renderPanelHeader}
      isLightDismiss
      isOpen={isOpen}
      onDismiss={dismissPanel}
      type={PanelType.medium}
      closeButtonAriaLabel="Close"
      styles={{
        navigation: {
          justifyContent: "space-between",
          alignItems: "center"
        },
        scrollableContent: {
          display: "flex",
          flexDirection: "column",
          height: "100%"
        },
        content: {
          flex: 1
        }
      }}
    >
      {renderSpinner() || renderInsightsContent()}
    </Panel>
  );

  return (
    <Fragment>
      {renderAIInsightsButton()}
      {renderPanel()}
    </Fragment>
  );
};

export default AIInsightsButtonWithPanel;
